import { useState, useCallback, useRef } from 'react';
import { uberDirectService } from '../services/UberDirectService';
import { useCart } from './useCart';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { VendorQuote, CartProduct } from '../types/delivery';
import { useDeliveryAddressContext } from '../contexts/DeliveryAddressContext';
import { DELIVERY_CONFIG, DeliveryHelpers } from '../config/deliveryConfig';

async function geocodeAddress(address: string): Promise<{ lat: number; lng: number }> {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY}`
    );
    const data = await response.json();
    
    if (data.results && data.results[0] && data.results[0].geometry) {
      return {
        lat: data.results[0].geometry.location.lat,
        lng: data.results[0].geometry.location.lng
      };
    }
    throw new Error('No coordinates found for address');
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
}

// Address validation moved to deliveryConfig.ts

export function useDelivery() {
  const { user } = useAuth();
  const { cartItems } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryQuote, setDeliveryQuote] = useState<VendorQuote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { selectedAddress } = useDeliveryAddressContext();
  
  // Track last quote request time to prevent rapid-fire requests
  const lastQuoteRequestRef = useRef<number>(0);
  const QUOTE_REQUEST_COOLDOWN = 5000; // 5 seconds minimum between quote requests (increased to reduce rate limiting)

  const getQuoteForCart = useCallback(async (customCartItems?: any[]) => {
    const itemsToUse = customCartItems || cartItems;
    if (!user || !itemsToUse.length) return;

    // Rate limiting: Don't request quotes too frequently
    const timeSinceLastRequest = Date.now() - lastQuoteRequestRef.current;
    if (timeSinceLastRequest < QUOTE_REQUEST_COOLDOWN) {
      const waitTime = QUOTE_REQUEST_COOLDOWN - timeSinceLastRequest;
      console.log(`[useDelivery] Rate limiting: waiting ${waitTime}ms before quote request`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    lastQuoteRequestRef.current = Date.now();

    setIsLoading(true);
    setError(null);
    try {
      if (!selectedAddress) {
        throw new Error('Delivery address not set');
      }

      if (!selectedAddress.street_address) {
        throw new Error('Delivery street address is required');
      }

      const dropoff_address = {
        address: selectedAddress.street_address,
        city: selectedAddress.city,
        state: selectedAddress.state,
        zip_code: selectedAddress.zip_code,
        coordinates: {
          lat: selectedAddress.latitude,
          lng: selectedAddress.longitude
        }
      };

      const itemsByVendor = itemsToUse.reduce<Record<string, { items: CartProduct[], vendor: CartProduct['product']['vendor'] }>>((acc, item) => {
        const vendorId = item.product.vendor_id;
        if (!acc[vendorId]) {
          acc[vendorId] = {
            items: [],
            vendor: item.product.vendor
          };
        }
        acc[vendorId].items.push(item);
        return acc;
      }, {});

      const quotes = await Promise.all(
        Object.entries(itemsByVendor).map(async ([vendorId, group]) => {
          try {
            // Try to use vendor data from cart items first (if available and complete)
            let vendorData = group.vendor;
            const hasCompleteVendorData = vendorData && 
              vendorData.street_line1 && 
              vendorData.city && 
              vendorData.state && 
              vendorData.zip_code;

            // If vendor data from cart is incomplete, fetch from database
            if (!hasCompleteVendorData) {
              const { data: fetchedVendorData, error: vendorError } = await supabase
                .from('vendors')
                .select('*, user_id, delivery_hours')
                .eq('id', vendorId)
                .single();

              if (vendorError || !fetchedVendorData?.street_line1) {
                throw new Error(`Failed to get vendor address for ${group.vendor?.store_name || 'vendor'}`);
              }

              vendorData = fetchedVendorData;
            } else {
              // If we have complete vendor data but missing delivery_hours, fetch it
              if (!(vendorData as any)?.delivery_hours) {
                const { data: hoursData } = await supabase
                  .from('vendors')
                  .select('delivery_hours')
                  .eq('id', vendorId)
                  .single();
                if (hoursData?.delivery_hours) {
                  (vendorData as any).delivery_hours = hoursData.delivery_hours;
                }
              }
            }

            console.log('Vendor Data:', vendorData);
            console.log('group.items', group.items)

            // Get buyer's phone number from their profile
            const { data: buyerProfile } = await supabase
              .from('profiles')
              .select('phone_number')
              .eq('id', user.id)
              .single();

            // Check vendor phone number (use from cart item vendor if available, otherwise from fetched data)
            const vendorPhoneNumber = (vendorData as any)?.phone_number || (group.vendor as any)?.phone_number;
            if (!vendorPhoneNumber) {
              throw new Error(`Vendor "${group.vendor?.store_name || 'Unknown'}" phone number is required for delivery. Please contact the vendor.`);
            }

            if (!buyerProfile?.phone_number) {
              throw new Error('Your phone number is required for delivery. Please add it to your profile.');
            }

            const pickupAddress = {
              street_address: [
                (vendorData.street_line1 || group.vendor?.street_line1 || '') + 
                ((vendorData as any).apartment ? ` #${(vendorData as any).apartment}` : '')
              ],
              city: vendorData.city || group.vendor?.city || '',
              state: vendorData.state || group.vendor?.state || '',
              zip_code: vendorData.zip_code || group.vendor?.zip_code || '',
              country: 'US' 
            };

            const dropoffAddress = {
              street_address: [
                selectedAddress.street_address + (selectedAddress.apartment ? ` #${selectedAddress.apartment}` : '')
              ],
              city: selectedAddress.city,
              state: selectedAddress.state,
              zip_code: selectedAddress.zip_code,
              country: DELIVERY_CONFIG.address.DEFAULT_COUNTRY
            };
            console.log('Pickup Address:', pickupAddress);
            console.log('Dropoff Address:', dropoffAddress);

            const formattedManifestItems = group.items.map(item => {
              const quantity = Number(item.quantity) || 0;
              
              if (!quantity || quantity <= 0) {
                throw new Error(`Invalid quantity for product: ${item.product.title}`);
              }
              
              return DeliveryHelpers.createManifestItem(
                item.product.title,
                quantity
              );
            });

            console.log('Manifest Items:', formattedManifestItems);

            const quote = await uberDirectService.getDeliveryQuote({
              pickup_address: pickupAddress,
              dropoff_address: dropoffAddress,
              pickup_latitude: Number((vendorData as any).latitude || (group.vendor as any)?.latitude || 0) || 0,
              pickup_longitude: Number((vendorData as any).longitude || (group.vendor as any)?.longitude || 0) || 0,
              dropoff_latitude: Number(selectedAddress.latitude) || 0,
              dropoff_longitude: Number(selectedAddress.longitude) || 0,
              manifest_items: formattedManifestItems,
              pickup_phone_number: vendorPhoneNumber,
              dropoff_phone_number: buyerProfile.phone_number,
            });

            const vendorQuote: VendorQuote = {
              vendorId,
              vendor_name: group.vendor?.store_name || 'Unknown Vendor',
              quote: {
                quote_id: quote.quote_id,
                fee: quote.fee,
                dropoff_eta: quote.dropoff_eta,
                expires_at: quote.expires,
                tracking_url: quote.tracking_url,
                pickup_phone_number: vendorPhoneNumber,
                dropoff_phone_number: buyerProfile.phone_number,
              },
              delivery_hours: (vendorData as any)?.delivery_hours || null,
              // Include vendor location for timezone-based store hours validation
              vendor_city: (vendorData as any)?.city,
              vendor_state: (vendorData as any)?.state,
              vendor_country: (vendorData as any)?.country || 'US',
            };

            return vendorQuote;
          } catch (error: any) {
            const vendorName = group.vendor?.store_name || 'Unknown Vendor';
            console.error('Error getting quote for vendor:', vendorId, vendorName, error);
            
            // Extract error message with better handling
            let errorMessage = error?.message || 'Failed to get delivery quote';
            
            // Handle edge function errors more gracefully
            if (errorMessage.includes('Edge Function returned a non-2xx status code')) {
              // Try multiple ways to extract error details from Supabase edge function error
              let extractedError = null;
              let extractedMessage = null;
              
              // Method 1: Check error.context.response.data (common structure)
              if (error?.context?.response?.data) {
                extractedError = error.context.response.data.error;
                extractedMessage = error.context.response.data.message;
              }
              
              // Method 2: Check error.data directly
              if (!extractedError && error?.data) {
                extractedError = error.data.error;
                extractedMessage = error.data.message;
              }
              
              // Method 3: Check error.response.data
              if (!extractedError && error?.response?.data) {
                extractedError = error.response.data.error;
                extractedMessage = error.response.data.message;
              }
              
              // Use extracted error details if available
              if (extractedError === 'rate_limited') {
                errorMessage = extractedMessage || 'Rate limited by Uber API. Please wait a moment and try again.';
              } else if (extractedError) {
                errorMessage = extractedMessage || extractedError || 'Delivery quote unavailable for this vendor.';
              } else if (extractedMessage) {
                errorMessage = extractedMessage;
              } else {
                errorMessage = 'Delivery quote unavailable for this vendor. The address may be outside the delivery zone.';
              }
            }
            
            // Provide user-friendly message for rate limiting
            if (errorMessage.includes('Rate limited') || errorMessage.includes('rate limit') || errorMessage.includes('too_many_requests') || errorMessage.includes('429')) {
              return {
                vendorId,
                vendor_name: vendorName,
                error: 'Delivery quote service is temporarily rate limited. Please wait a moment and try again.'
              } as VendorQuote;
            }
            
            // Provide user-friendly message for serviceability issues
            if (errorMessage.includes('not serviceable') || errorMessage.includes('outside') || errorMessage.includes('unavailable')) {
              return {
                vendorId,
                vendor_name: vendorName,
                error: 'Delivery unavailable for this vendor. Address may be outside the delivery zone.'
              } as VendorQuote;
            }
            
            return {
              vendorId,
              vendor_name: vendorName,
              error: errorMessage
            } as VendorQuote;
          }
        })
      );

      setDeliveryQuote(quotes);
      return quotes;
    } catch (error: any) {
      console.error('Delivery quote error:', error);
      const errorMessage = error?.message || 'Failed to get delivery quote';
      
      // Provide user-friendly message for rate limiting
      if (errorMessage.includes('Rate limited') || errorMessage.includes('too_many_requests')) {
        const friendlyMessage = 'Delivery quote service is temporarily rate limited. Please wait a moment and try again.';
        setError(friendlyMessage);
        throw new Error(friendlyMessage);
      }
      
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, cartItems, selectedAddress]); // Keep cartItems in dependency array as it's from context

  // Distance calculation moved to deliveryConfig.ts

  return {
    isLoading,
    deliveryQuote,
    error,
    getQuoteForCart
  };
} 